import { useEffect, useMemo, useState } from 'react'

import { useCourses, type Lesson } from '@/api/courses'
import { useAuth } from '@/auth/AuthProvider'
import { Badge, Card, CardHeader, EmptyState, ScreenHeader, cn } from '@/components/ui'
import { useRouter } from '@/routes'
import { LoadingLine, RunErrorCard } from '@/screens/Case/runParts'

import { coursePath, parseCoursePath } from './coursesPath'
import { LessonPlayer } from './LessonPlayer'
import { readProgress, type LessonProgress } from './progress'

/**
 * Courses — recorded lessons on how to use this platform, for whoever is
 * signed in.
 *
 * The lessons are a folder on the server (`TMVERSE_COURSES_DIR`, see
 * `server/app/services/courses.py`), so a re-recorded lesson is a file copy and
 * not a deploy. This screen asks what is there and plays it:
 *
 * - the **list** in order, each with its length, who it is for, and a "Watch
 *   first" link where the lesson builds on another;
 * - the **player**, with captions, chapters that seek and highlight as the
 *   video plays, and a searchable transcript (`LessonPlayer`);
 * - a quiet **tick** for what has been watched, and "Resume from" for what has
 *   been started — remembered in this browser only (`progress.ts`).
 *
 * `/courses` opens the first lesson; `/courses/M03` opens that one, which is
 * what makes a lesson linkable from the User guide or an email.
 *
 * Not set up, unreadable and empty are three different sentences, because they
 * have three different fixes — and only an administrator is told the cause.
 */
export function CoursesScreen() {
  const router = useRouter()
  const { user, isAdmin } = useAuth()
  const courses = useCourses()
  const userKey = user?.email ?? 'anonymous'
  const [progress, setProgress] = useState<Record<string, LessonProgress>>(() =>
    readProgress(userKey),
  )
  // Who is signed in can arrive a moment after this screen mounts; progress is
  // per person, so it is read again for whoever that turns out to be.
  useEffect(() => {
    setProgress(readProgress(userKey))
  }, [userKey])

  const lessons = useMemo(() => courses.data?.lessons ?? [], [courses.data])
  const requested = parseCoursePath(router.path)
  const selected = lessons.find((lesson) => lesson.id === requested) ?? lessons[0]

  return (
    <div className="flex flex-col gap-6">
      <ScreenHeader
        title="Courses"
        description="Short recorded lessons on using TM-Traverse, from reading an office action to signing the letter. Everything shown in them is invented demo data."
      />

      {courses.isPending ? <LoadingLine label="Loading the lessons…" /> : null}
      {courses.isError ? <RunErrorCard title="Could not load the courses" error={courses.error} /> : null}

      {courses.data && !courses.data.configured ? (
        <EmptyState
          title="Courses are not set up on this server yet"
          description={
            isAdmin
              ? 'Set TMVERSE_COURSES_DIR to the folder holding the lessons (the .mp4 and .vtt files and the Courses_guide.html), then reload this page.'
              : 'An administrator needs to add the lesson videos to the server. Ask them, and they will appear here.'
          }
        />
      ) : null}

      {courses.data && courses.data.configured && !courses.data.available ? (
        <EmptyState
          title="The courses folder cannot be read"
          description={
            isAdmin
              ? 'The folder named by TMVERSE_COURSES_DIR is missing or not readable by the server.'
              : 'The lessons are not available right now. Tell an administrator.'
          }
        >
          <Problems problems={courses.data.problems} />
        </EmptyState>
      ) : null}

      {courses.data?.available && lessons.length === 0 ? (
        <EmptyState
          title="No lessons have been added yet"
          description="The courses folder is there but holds no lessons. Copy the lesson videos into it and they will appear here."
        >
          <Problems problems={courses.data.problems} />
        </EmptyState>
      ) : null}

      {selected ? (
        <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
          {/* On a phone the lesson you are on comes first and the list after it;
              from `lg` up they sit side by side, list on the left. */}
          <div className="order-2 lg:order-1">
            <LessonList
              lessons={lessons}
              selectedId={selected.id}
              progress={progress}
              onSelect={(code) => router.navigate(coursePath(code))}
            />
          </div>
          <div className="order-1 flex min-w-0 flex-col gap-4 lg:order-2">
            <WatchFirst
              lesson={selected}
              lessons={lessons}
              progress={progress}
              onSelect={(code) => router.navigate(coursePath(code))}
            />
            <LessonPlayer
              key={selected.id}
              lesson={selected}
              userKey={userKey}
              saved={progress[selected.id]}
              onProgress={(code, next) => setProgress((current) => ({ ...current, [code]: next }))}
            />
            {courses.data?.problems.length ? (
              <Card className="gap-1">
                <CardHeader title="For administrators" />
                <Problems problems={courses.data.problems} />
              </Card>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function Problems({ problems }: { problems: string[] }) {
  if (problems.length === 0) return null
  return (
    <ul className="m-0 list-disc pl-5 text-meta text-muted">
      {problems.map((problem) => (
        <li key={problem}>{problem}</li>
      ))}
    </ul>
  )
}

function LessonList({
  lessons,
  selectedId,
  progress,
  onSelect,
}: {
  lessons: Lesson[]
  selectedId: string
  progress: Record<string, LessonProgress>
  onSelect: (code: string) => void
}) {
  return (
    <nav aria-label="Lessons" className="flex flex-col gap-2">
      <ol className="m-0 flex list-none flex-col gap-2 p-0">
        {lessons.map((lesson) => {
          const done = progress[lesson.id]?.watched
          const active = lesson.id === selectedId
          return (
            <li key={lesson.id}>
              <button
                type="button"
                aria-current={active ? 'true' : undefined}
                onClick={() => onSelect(lesson.id)}
                className={cn(
                  'flex w-full flex-col gap-1 rounded-md border px-3 py-2 text-left',
                  active
                    ? 'border-brand-700 bg-brand-50'
                    : 'border-divider bg-surface hover:bg-ground',
                )}
              >
                <span className="flex items-center gap-2 text-micro text-muted">
                  <span className="font-mono">{lesson.id}</span>
                  {lesson.duration ? <span>{lesson.duration}</span> : null}
                  {done ? <Badge tone="ok">Watched</Badge> : null}
                  {!lesson.has_video ? <Badge tone="outline">Video to come</Badge> : null}
                </span>
                <span className="text-body font-medium">{lesson.title}</span>
                {lesson.audience ? (
                  <span className="text-micro text-muted">{lesson.audience}</span>
                ) : null}
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

/** "Watch first: M01 …" — the lessons this one builds on, as links. */
function WatchFirst({
  lesson,
  lessons,
  progress,
  onSelect,
}: {
  lesson: Lesson
  lessons: Lesson[]
  progress: Record<string, LessonProgress>
  onSelect: (code: string) => void
}) {
  const before = lesson.prerequisites
    .map((code) => lessons.find((candidate) => candidate.id === code))
    .filter((item): item is Lesson => item !== undefined)
  if (before.length === 0) return null
  return (
    <p className="m-0 flex flex-wrap items-center gap-2 text-meta text-muted">
      <span>Watch first:</span>
      {before.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onSelect(item.id)}
          className="rounded-full border border-divider px-2.5 py-0.5 text-micro text-brand-700 hover:bg-ground"
        >
          {item.id} · {item.title}
          {progress[item.id]?.watched ? ' ✓' : ''}
        </button>
      ))}
    </p>
  )
}

